<template>
	<div
		class="flex flex-col items-start justify-between gap-4 pb-4 md:flex-row md:items-center md:gap-0"
	>
		<span
			class="text-start text-sm font-medium uppercase tracking-[0.125rem] text-base-content"
		>
			{{ $t('We are trusted by companies worldwide') }}
		</span>
		<div class="flex items-center gap-[0.625rem]">
			<img :src="ClutchIcon" alt="Clutch Icon" />
			<div class="flex gap-0.5">
				<StarIcon v-for="index in 5" :key="index" class="size-5 text-black" />
			</div>
			<span class="text-sm text-black">50+ reviews</span>
			<a href="https://clutch.co/profile/jagaad">
				<ArrowUpRightIcon class="size-4 stroke-2 text-black" />
			</a>
		</div>
	</div>
	<div class="relative overflow-visible">
		<!-- Desktop navigation buttons -->
		<div
			class="absolute top-1/2 z-10 hidden max-w-screen-3xl -translate-y-1/2 items-center justify-between lg:-ml-8 lg:flex lg:w-[103%]"
		>
			<button
				:class="[
					'flex size-12 items-center justify-center rounded-full border border-base-200 bg-white text-black transition-colors duration-200 hover:bg-primary hover:text-white',
					isFirstSlide && 'pointer-events-none invisible',
				]"
				:disabled="isFirstSlide"
				@click="prevPartner"
			>
				<ArrowLeftIcon class="size-5" />
			</button>
			<button
				:class="[
					'flex size-12 items-center justify-center rounded-full border border-base-200 bg-white text-black transition-colors duration-200 hover:bg-primary hover:text-white',
					isLastSlide && 'pointer-events-none invisible',
				]"
				@click="nextPartner"
			>
				<ArrowRightIcon class="size-5" />
			</button>
		</div>

		<!-- Swiper container -->
		<div
			ref="swiperContainer"
			class="swiper-partners-container relative w-full overflow-visible"
			:style="containerStyle"
		>
			<div class="swiper-wrapper">
				<div
					v-for="(partner, index) in cardsData"
					:key="index"
					class="swiper-slide shrink-0 rounded-lg bg-white px-3 py-2 md:px-5 md:py-3"
					:style="slideStyle"
				>
					<div class="flex h-full w-full items-center justify-center">
						<img
							:src="partner.image"
							alt="partner"
							class="max-h-full max-w-full object-contain"
						/>
					</div>
				</div>
			</div>
		</div>

		<!-- Mobile navigation -->
		<div
			:class="[
				'mx-auto flex max-w-[37.5rem] items-center justify-center gap-3 pt-4 lg:hidden',
				{ hidden: isFirstSlide && isLastSlide },
			]"
		>
			<button
				:class="[
					'flex size-8 flex-shrink-0 items-center justify-center rounded-full transition-colors duration-200',
					isFirstSlide ? 'bg-white/30 text-black' : 'bg-white text-black',
				]"
				@click="prevPartner"
			>
				<ArrowLeftIcon class="size-5" />
			</button>

			<div class="bar relative h-1 max-w-full flex-1 bg-white/20">
				<div
					class="absolute h-full bg-white transition-all duration-300"
					:style="progressBarStyle"
				></div>
			</div>

			<button
				:class="[
					'flex size-8 flex-shrink-0 items-center justify-center rounded-full transition-colors duration-200',
					isLastSlide ? 'bg-white/30 text-black' : 'bg-white text-black',
				]"
				@click="nextPartner"
			>
				<ArrowRightIcon class="size-5" />
			</button>
		</div>
	</div>
</template>

<script lang="ts" setup>
import { cardsData } from './section-data';
import {
	SPACE_BETWEEN,
	calculateDynamicWidth as calculateDynamicWidthUtil,
	getSwiperMetrics,
} from './utils';
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import ClutchIcon from '@/images/icons/clutch.svg?url';
import { StarIcon } from '@heroicons/vue/20/solid';
import { ArrowUpRightIcon } from '@heroicons/vue/24/outline';
import { ArrowLeftIcon, ArrowRightIcon } from '@heroicons/vue/24/outline';
import { useResizeObserver } from '@vueuse/core';
import Swiper from 'swiper';
import 'swiper/css';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore - Swiper CSS modules are handled by the bundler
import 'swiper/css/grid';
import { Grid, Navigation } from 'swiper/modules';

const activeIndex = ref(0);
const isAtEnd = ref(false);
let partnersSwiper: Swiper | null = null;
const swiperContainer = ref<HTMLElement | null>(null);
const containerWidth = ref(0);
const dynamicCardWidth = ref<number | null>(null);

const isFirstSlide = computed(() => activeIndex.value === 0);
const isLastSlide = computed(() => isAtEnd.value);
const totalColumns = ref(0);
const visibleColumns = ref(0);
const currentColumn = ref(0);
const maxColumn = ref(0);

function updateSwiperState(swiper: Swiper) {
	const total = cardsData.length;

	if (total === 0) {
		activeIndex.value = 0;
		isAtEnd.value = true;
		totalColumns.value = 0;
		visibleColumns.value = 0;
		currentColumn.value = 0;
		maxColumn.value = 0;
		return;
	}

	const {
		currentColumn: current,
		maxColumn: max,
		visibleColumns: visible,
		totalColumns: totalCols,
	} = getSwiperMetrics(swiper, total);

	activeIndex.value = swiper.activeIndex;
	totalColumns.value = totalCols;
	visibleColumns.value = visible;

	currentColumn.value = Math.min(current, max);
	maxColumn.value = max;

	isAtEnd.value = totalCols <= visible || current >= max;
}

const progressBarStyle = computed(() => {
	const barTotalColumns = totalColumns.value;
	const barVisibleColumns = visibleColumns.value;
	const barCurrentColumn = currentColumn.value;
	const barMaxColumn = maxColumn.value;

	if (!partnersSwiper) {
		return { width: '100%', left: '0%' };
	}

	const total = cardsData.length;
	if (total <= 1) return { width: '100%', left: '0%' };

	if (barTotalColumns <= barVisibleColumns) {
		return { width: '100%', left: '0%' };
	}

	const barWidth = (barVisibleColumns / barTotalColumns) * 100;

	const clampedColumn = Math.min(barCurrentColumn, barMaxColumn);

	const scrollableRange = barMaxColumn;
	const left =
		scrollableRange > 0
			? (clampedColumn / scrollableRange) * (100 - barWidth)
			: 0;

	return { width: `${barWidth}%`, left: `${left}%` };
});

function calculateDynamicWidth() {
	if (!swiperContainer.value || !partnersSwiper) {
		dynamicCardWidth.value = null;
		return;
	}

	const totalCards = cardsData.length;
	const containerW = containerWidth.value || swiperContainer.value.offsetWidth;

	dynamicCardWidth.value = calculateDynamicWidthUtil(containerW, totalCards);
}

const slideStyle = computed(() => {
	return {};
});

const containerStyle = computed(() => {
	if (dynamicCardWidth.value) {
		return {
			'--dynamic-card-width': `${dynamicCardWidth.value}px`,
		};
	}
	return {};
});

const isMobile = computed(() => {
	return containerWidth.value < 768;
});

watch([containerWidth, () => cardsData.length, dynamicCardWidth], () => {
	if (partnersSwiper && swiperContainer.value) {
		// Force Swiper to recalculate slide widths
		setTimeout(() => {
			if (partnersSwiper && swiperContainer.value) {
				const slides = Array.from(
					swiperContainer.value.querySelectorAll('.swiper-slide'),
				) as HTMLElement[];
				if (dynamicCardWidth.value && slides.length > 0) {
					slides.forEach((slide) => {
						slide.style.width = `${dynamicCardWidth.value}px`;
						// Set height based on screen size
						slide.style.height = isMobile.value ? '4.5rem' : '110px';
					});
				} else {
					// Reset to default if no dynamic width
					slides.forEach((slide) => {
						slide.style.width = '';
						slide.style.height = '';
					});
				}
				partnersSwiper.update();
				partnersSwiper.updateSlides();
				partnersSwiper.updateSize();
				updateSwiperState(partnersSwiper);
			}
		}, 50);
	}
});

const { stop: stopResizeObserver } = useResizeObserver(swiperContainer, () => {
	if (swiperContainer.value) {
		containerWidth.value = swiperContainer.value.offsetWidth;
		calculateDynamicWidth();
	}
});

function nextPartner() {
	if (partnersSwiper && !isAtEnd.value) {
		partnersSwiper.slideNext();
	}
}

function prevPartner() {
	if (partnersSwiper && activeIndex.value > 0) {
		partnersSwiper.slidePrev();
	}
}

onMounted(() => {
	if (swiperContainer.value) {
		containerWidth.value = swiperContainer.value.offsetWidth;
	}

	partnersSwiper = new Swiper('.swiper-partners-container', {
		modules: [Navigation, Grid],
		slidesPerView: 'auto',
		grid: {
			rows: 1,
			fill: 'row',
		},
		spaceBetween: SPACE_BETWEEN,
		centeredSlides: false,
		loop: false,
		breakpoints: {
			0: {
				slidesPerView: 'auto',
				grid: {
					rows: 2,
					fill: 'column',
				},
				spaceBetween: SPACE_BETWEEN,
			},
			768: {
				slidesPerView: 'auto',
				grid: {
					rows: 1,
					fill: 'row',
				},
				spaceBetween: SPACE_BETWEEN,
			},
		},
		on: {
			slideChange(this: Swiper) {
				updateSwiperState(this);
			},
			progress(this: Swiper) {
				updateSwiperState(this);
			},
			resize() {
				if (!partnersSwiper || !swiperContainer.value) return;
				containerWidth.value = swiperContainer.value.offsetWidth;
				calculateDynamicWidth();
				setTimeout(() => {
					if (partnersSwiper && swiperContainer.value) {
						if (dynamicCardWidth.value) {
							const slides = Array.from(
								swiperContainer.value.querySelectorAll('.swiper-slide'),
							) as HTMLElement[];
							const isMobileSize = containerWidth.value < 768;
							slides.forEach((slide) => {
								slide.style.width = `${dynamicCardWidth.value}px`;
								slide.style.height = isMobileSize ? '4.5rem' : '110px';
							});
						} else {
							const slides = Array.from(
								swiperContainer.value.querySelectorAll('.swiper-slide'),
							) as HTMLElement[];
							slides.forEach((slide) => {
								slide.style.width = '';
								slide.style.height = '';
							});
						}
						partnersSwiper.update();
						partnersSwiper.updateSlides();
						partnersSwiper.updateSize();
						updateSwiperState(partnersSwiper);
					}
				}, 50);
			},
		},
	});

	// Wait for Swiper to initialize, then calculate and update
	setTimeout(() => {
		if (swiperContainer.value) {
			containerWidth.value = swiperContainer.value.offsetWidth;
		}
		calculateDynamicWidth();
		if (partnersSwiper && swiperContainer.value) {
			const slides = Array.from(
				swiperContainer.value.querySelectorAll('.swiper-slide'),
			) as HTMLElement[];
			const isMobileSize = containerWidth.value < 768;
			if (dynamicCardWidth.value && slides.length > 0) {
				slides.forEach((slide) => {
					slide.style.width = `${dynamicCardWidth.value}px`;
					slide.style.height = isMobileSize ? '4.5rem' : '110px';
				});
			} else {
				slides.forEach((slide) => {
					slide.style.width = '';
					slide.style.height = '';
				});
			}
			partnersSwiper.update();
			partnersSwiper.updateSlides();
			partnersSwiper.updateSize();
			updateSwiperState(partnersSwiper);
		}
	}, 150);
});

onUnmounted(() => {
	stopResizeObserver();
});
</script>

<style scoped>
.swiper-slide {
	height: 4.5rem;
	width: var(--dynamic-card-width, 9.25rem);
}

@media (min-width: 768px) {
	.swiper-slide {
		height: 110px;
		width: var(--dynamic-card-width, 230px);
	}
}

@media (max-width: 767px) {
	.swiper-partners-container {
		height: 11rem;
	}
}
</style>
